import { useEffect, useState } from "react";
import { getMySubmittedWarrantyQuotes } from "@/Calls/callWarrantyAuth";
import { Card } from "@/components/ui/card";
import { Loader2, FileText } from "lucide-react";

const STATUS_COLOR: Record<string, string> = {
    PENDING:  "bg-yellow-100 text-yellow-700",
    ACCEPTED: "bg-green-100 text-green-700",
    DECLINED: "bg-red-100 text-red-700",
    EXPIRED:  "bg-gray-100 text-gray-600",
};

export default function WarrantyMyQuotesPage() {
    const [quotes,  setQuotes]  = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMySubmittedWarrantyQuotes()
            .then((r) => setQuotes(r.data.quotes ?? []))
            .catch(() => setQuotes([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">My Submitted Quotes</h1>
                <p className="text-muted-foreground text-sm mt-1">{quotes.length} quote{quotes.length !== 1 ? "s" : ""}</p>
            </div>

            {quotes.length === 0 ? (
                <Card className="p-12 text-center border-dashed">
                    <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">No quotes submitted yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Pull leads and submit coverage quotes to see them here</p>
                </Card>
            ) : (
                <div className="space-y-3">
                    {quotes.map((q) => (
                        <Card key={q.quote_id} className="p-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium">
                                            {q.coverage_type?.replace("_", " ")} Coverage
                                        </p>
                                        <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${STATUS_COLOR[q.status] ?? "bg-gray-100"}`}>
                                            {q.status}
                                        </span>
                                    </div>
                                    <div className="text-sm text-muted-foreground space-y-0.5">
                                        {q.coverage_months && <p>{q.coverage_months} months / {q.coverage_miles ?? "unlimited"} miles</p>}
                                        {q.monthly_cost && <p>Monthly: ${q.monthly_cost}</p>}
                                        {q.deductible && <p>Deductible: ${q.deductible}</p>}
                                        {q.contract_number && <p>Contract: {q.contract_number}</p>}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Request #{q.request_id} · Submitted {new Date(q.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-lg font-bold">${q.monthly_cost ?? "—"}/mo</p>
                                    {q.contract_url && (
                                        <a
                                            href={q.contract_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs text-purple-600 hover:underline"
                                        >
                                            View Contract
                                        </a>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
